import React, { useMemo, useRef, useState } from "react";
import {
    Box,
    Typography,
    Stack,
    Chip,
    Tooltip,
    Autocomplete,
    TextField,
    Button,
    Paper,
    Divider,
} from "@mui/material";
import { Add, DeleteOutline, Public } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

import { countries } from "../../../constants/countres";

/**
 * Editable list of {country, state} pairs.
 * Used inside the zone form and inside the standalone "sync locations" dialog.
 */
const LocationsEditor = ({ locations = [], onChange }) => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || "en").startsWith("ar");

    const [country, setCountry] = useState(null);
    const [state, setState] = useState("");
    const countryInputRef = useRef(null);

    const countryByCode = useMemo(() => {
        const map = {};
        countries.forEach((c) => { map[c.code] = c; });
        return map;
    }, []);

    const addLocation = () => {
        if (!country) return;
        const exists = locations.some(
            (loc) =>
                (loc.country_code || loc.country) === country.code &&
                (loc.state || "") === (state || "")
        );
        if (exists) {
            // Already added; just clear and refocus.
            setCountry(null);
            setState("");
            countryInputRef.current?.focus();
            return;
        }
        onChange([
            ...locations,
            { country_code: country.code, state: state || null },
        ]);
        setCountry(null);
        setState("");
        // Refocus the country picker so the admin can keep adding.
        countryInputRef.current?.focus();
    };

    const removeAt = (index) => {
        onChange(locations.filter((_, i) => i !== index));
    };

    const clearAll = () => {
        onChange([]);
    };

    const handleKeyDown = (e) => {
        // Quality-of-life: Enter on either field commits.
        if (e.key === "Enter" && country) {
            e.preventDefault();
            addLocation();
        }
    };

    return (
        <Box>
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                mb={1.5}
            >
                <Stack direction="row" spacing={1} alignItems="center">
                    <Public fontSize="small" color="primary" />
                    <Typography variant="subtitle2" fontWeight={700}>
                        {isArabic ? "المواقع المغطاة" : "Covered Locations"}
                    </Typography>
                    <Chip
                        size="small"
                        label={locations.length}
                        color={locations.length > 0 ? "primary" : "default"}
                        variant="tonal"
                        sx={{ fontWeight: "bold", borderRadius: "6px", minWidth: "32px" }}
                    />
                </Stack>

                {locations.length > 0 && (
                    <Button size="small" color="error" onClick={clearAll}>
                        {isArabic ? "مسح الكل" : "Clear all"}
                    </Button>
                )}
            </Stack>

            <Paper
                variant="outlined"
                sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: (t) =>
                        t.palette.mode === "dark"
                            ? "rgba(255,255,255,0.02)"
                            : "rgba(0,0,0,0.01)",
                }}
            >
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1.5}
                    alignItems={{ sm: "flex-start" }}
                    onKeyDown={handleKeyDown}
                >
                    <Autocomplete
                        size="small"
                        sx={{ flex: 1, minWidth: 220 }}
                        options={countries}
                        value={country}
                        onChange={(_, val) => setCountry(val)}
                        getOptionLabel={(opt) => opt?.label || ""}
                        isOptionEqualToValue={(a, b) => a?.code === b?.code}
                        renderOption={(props, option) => (
                            <Box component="li" {...props} key={option.code}>
                                <Typography variant="body2" sx={{ flex: 1 }}>
                                    {option.label}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {option.code}
                                </Typography>
                            </Box>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                inputRef={countryInputRef}
                                label={isArabic ? "الدولة" : "Country"}
                                placeholder={isArabic ? "ابحث..." : "Search..."}
                            />
                        )}
                    />

                    <TextField
                        size="small"
                        sx={{ flex: 1 }}
                        label={
                            isArabic
                                ? "المنطقة / الولاية (اختياري)"
                                : "State / Region (optional)"
                        }
                        value={state}
                        onChange={(e) => setState(e.target.value)}
                        disabled={!country}
                        helperText={
                            !country
                                ? (isArabic ? "اختر دولة أولاً" : "Pick a country first")
                                : ""
                        }
                    />

                    <Tooltip
                        title={isArabic ? "إضافة موقع" : "Add location"}
                        placement="top"
                    >
                        <span>
                            <Button
                                variant="contained"
                                onClick={addLocation}
                                disabled={!country}
                                startIcon={<Add />}
                                sx={{
                                    height: 40,
                                    minWidth: 110,
                                    borderRadius: 2,
                                    fontWeight: "bold",
                                    flexShrink: 0,
                                }}
                            >
                                {isArabic ? "إضافة" : "Add"}
                            </Button>
                        </span>
                    </Tooltip>
                </Stack>

                {locations.length > 0 && (
                    <>
                        <Divider sx={{ my: 2 }} />
                        <Stack direction="row" flexWrap="wrap" gap={1}>
                            {locations.map((loc, idx) => {
                                const code = loc.country_code || loc.country;
                                const c = countryByCode[code];
                                const label = c ? c.label : code;
                                return (
                                    <Chip
                                        key={`${code}-${loc.state || ""}-${idx}`}
                                        label={loc.state ? `${label} — ${loc.state}` : label}
                                        onDelete={() => removeAt(idx)}
                                        deleteIcon={<DeleteOutline />}
                                        color="primary"
                                        variant="tonal"
                                        sx={{
                                            borderRadius: "8px",
                                            fontWeight: "bold",
                                            py: 2,
                                        }}
                                    />
                                );
                            })}
                        </Stack>
                    </>
                )}

                {locations.length === 0 && (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ textAlign: "center", mt: 2 }}
                    >
                        {isArabic
                            ? "لم تتم إضافة أي مواقع بعد. اختر دولة وانقر إضافة."
                            : "No locations added yet. Pick a country and click Add."}
                    </Typography>
                )}
            </Paper>
        </Box>
    );
};

export default LocationsEditor;
